import React from "react";
import styled, { css } from "styled-components";
import { tokens } from "../../theme/tokens";

// Card / Panel — the standard rounded surface for content across the portal.
//
//   <Card>...</Card>
//   <Card padded={false}><CardHeader title="Riders" actions={<Button/>}/> ...</Card>
//
// props: padded (bool|false to remove default padding), interactive (hover lift),
//        tone ("default" | "muted" | "brand")

const { colors, radii, shadows, spacing, typography, motion } = tokens;

const tones = {
    default: css`
        background: ${colors.surface};
        border: 1px solid ${colors.border};
    `,
    muted: css`
        background: ${colors.surfaceMuted};
        border: 1px solid ${colors.border};
    `,
    brand: css`
        background: linear-gradient(135deg, ${colors.rose} 0%, ${colors.surface} 70%);
        border: 1px solid ${colors.line};
    `,
};

export const Card = styled.div`
    ${(p) => tones[p.tone] || tones.default};
    border-radius: ${radii.lg};
    box-shadow: ${shadows.sm};
    padding: ${(p) => (p.padded === false ? "0" : spacing[5])};
    transition: box-shadow ${motion.base} ${motion.ease},
        transform ${motion.base} ${motion.ease}, border-color ${motion.base} ${motion.ease};

    ${(p) =>
        p.interactive &&
        css`
            cursor: pointer;
            &:hover {
                box-shadow: ${shadows.md};
                transform: translateY(-3px);
                border-color: ${colors.line};
            }
        `};
`;

export const CardHeader = styled.div`
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: ${spacing[4]};
    padding: ${spacing[4]} ${spacing[5]};
    border-bottom: 1px solid ${colors.divider};
`;

const HeaderTitles = styled.div`
    min-width: 0;
    h3 {
        margin: 0;
        font-family: ${typography.fonts.body};
        font-size: ${typography.size.lg};
        font-weight: ${typography.weight.bold};
        color: ${colors.heading};
        letter-spacing: ${typography.tracking.tight};
    }
    p {
        margin: 4px 0 0;
        font-size: ${typography.size.sm};
        color: ${colors.muted};
    }
`;

const HeaderActions = styled.div`
    display: flex;
    align-items: center;
    gap: ${spacing[2]};
    flex-shrink: 0;
`;

export const CardBody = styled.div`
    padding: ${(p) => (p.padded === false ? "0" : spacing[5])};
`;

export const CardFooter = styled.div`
    display: flex;
    align-items: center;
    justify-content: ${(p) => p.align || "flex-end"};
    gap: ${spacing[2]};
    padding: ${spacing[4]} ${spacing[5]};
    border-top: 1px solid ${colors.divider};
`;

// Convenience header with title/subtitle/actions slots.
export function CardTitleBar({ title, subtitle, actions }) {
    return (
        <CardHeader>
            <HeaderTitles>
                <h3>{title}</h3>
                {subtitle && <p>{subtitle}</p>}
            </HeaderTitles>
            {actions && <HeaderActions>{actions}</HeaderActions>}
        </CardHeader>
    );
}

export default Card;
